import { chain, get, map, size } from "lodash"
import { VIEW_TYPES } from "helpers/base"

export const GROUPING = {
  ACTIVE_POSITION_ATTRIBUTE: "ACTIVE_POSITION_ATTRIBUTE",
  ACTIVE_TAG: "ACTIVE_TAG",
  MULTIPLE_INSTRUMENTS_POSITION_ATTRIBUTE: "MULTIPLE_INSTRUMENTS_POSITION_ATTRIBUTE",
  MULTIPLE_INSTRUMENTS_TAG: "MULTIPLE_INSTRUMENTS_TAG",
}

export const GROUPING_UNAVAILABLE = {
  [GROUPING.ACTIVE_POSITION_ATTRIBUTE]: "Grouping by position attribute is not available in Active mode.",
  [GROUPING.ACTIVE_TAG]: "Grouping by tag is not available in Active mode.",
  [GROUPING.MULTIPLE_INSTRUMENTS_POSITION_ATTRIBUTE]: "Grouping by position attribute is not available in dashboard with multiple instruments.",
  [GROUPING.MULTIPLE_INSTRUMENTS_TAG]: "Grouping by tag is not available in dashboard with multiple instruments.",
}

export const LONG_SHORT_GROUPING = {
  id: "LONG_SHORT",
  label: "Long / Short",
}

export const OVERWEIGHT_UNDERWEIGHT_GROUPING = {
  id: "OVERWEIGHT_UNDERWEIGHT",
  label: "Overweight / Underweight",
}

function getIsLongShortRelated(rollupId) {
  return rollupId === "LONG_SHORT" || rollupId === "OVERWEIGHT_UNDERWEIGHT"
}

const COMPOSITION_ROLLUPS = [{
  id: "COUNTRY",
  label: "Country",
}, {
  id: "CURRENCY",
  label: "Currency",
},
LONG_SHORT_GROUPING,
{
  id: "MODELED_UNMODELED",
  label: "Modeled / Unmodeled",
}]

const getCompositionRollup = (rollup, viewType) => {
  const isLongShortRelated = getIsLongShortRelated(rollup?.id)
  if (!isLongShortRelated) {
    return rollup
  }
  return viewType === VIEW_TYPES.ACTIVE ? OVERWEIGHT_UNDERWEIGHT_GROUPING : LONG_SHORT_GROUPING
}

export const getRollupSummaryLabel = rollup => {
  return getIsLongShortRelated(rollup?.id) ? "Group" : rollup?.label
}

function getAvailableRollups({ tagCategories = [], classifications = [], attributeCategories = [] }, selectedDate, viewType, modelRollups, hasMultipleUniqueInstruments = false) {
  // Handle licensed classifications with version filtering
  const licensedClassificationRollups = chain(classifications)
    .filter(c => !c.proprietary)
    .map(c => ({
      isClassification: true,
      label: c.name,
      id: c.id,
      options: chain(c.versions)
        .filter(v => v.asOf < selectedDate)
        .last()
        .get("tiers")
        .map(v => ({
          label: v.name,
          id: v.id,
          isClassification: true,
          classificationId: c.id,
        }))
        .value(),
    }))
    .value()

  // Handle proprietary classifications
  const pscRollupOptions = chain(classifications)
    .filter(c => c.proprietary)
    .map(c => ({
      isClassification: true,
      proprietary: true,
      label: c.name,
      id: c.id,
    }))
    .value()

  const pscRollups = []
  if (size(pscRollupOptions)) {
    pscRollups.push({
      label: "Personalized Classifications",
      options: pscRollupOptions,
    })
  }

  const disabledTagsAndAttributes = hasMultipleUniqueInstruments || viewType === VIEW_TYPES.ACTIVE

  // Handle tag categories
  const tagCategoryRollupOptions = map(tagCategories, category => ({
    isTag: true,
    label: category.name,
    id: category.id,
  }))

  const tagCategoryRollups = []
  if (size(tagCategoryRollupOptions)) {
    const disabledTooltip = hasMultipleUniqueInstruments ? GROUPING_UNAVAILABLE[GROUPING.MULTIPLE_INSTRUMENTS_TAG] : GROUPING_UNAVAILABLE[GROUPING.ACTIVE_TAG]
    tagCategoryRollups.push({
      label: "Tags",
      options: map(tagCategoryRollupOptions, option => ({
        ...option,
        disabled: disabledTagsAndAttributes,
        ...(disabledTagsAndAttributes && { disabledTooltip }),
      })),
    })
  }

  // Handle attribute categories
  const attributeCategoryRollupOptions = map(attributeCategories, ({ id, name }) => ({
    isPositionAttribute: true,
    label: name,
    id,
  }))

  const attributeCategoryRollups = []
  if (size(attributeCategoryRollupOptions)) {
    const disabledTooltip = hasMultipleUniqueInstruments ? GROUPING_UNAVAILABLE[GROUPING.MULTIPLE_INSTRUMENTS_POSITION_ATTRIBUTE] : GROUPING_UNAVAILABLE[GROUPING.ACTIVE_POSITION_ATTRIBUTE]
    attributeCategoryRollups.push({
      label: "Position Attributes",
      options: map(attributeCategoryRollupOptions, option => ({
        ...option,
        disabled: disabledTagsAndAttributes,
        ...(disabledTagsAndAttributes && { disabledTooltip }),
      })),
    })
  }

  return [
    ...modelRollups,
    ...licensedClassificationRollups,
    ...pscRollups,
    ...tagCategoryRollups,
    ...attributeCategoryRollups,
  ]
}

// [TODO:TS]: Add type to function useAvailableGroupByOptions in dashboard/helpers
export function getAllAvailableRollups({ tagCategories = [], classifications = [], attributeCategories = [] }, selectedDate, viewType, hasMultipleUniqueInstruments = false) {
  const modelRollups = [{
    label: "Composition",
    options: map(COMPOSITION_ROLLUPS, rollup => getCompositionRollup(rollup, viewType)),
  }]

  return getAvailableRollups({ tagCategories, classifications, attributeCategories }, selectedDate, viewType, modelRollups, hasMultipleUniqueInstruments)
}

/**
 * Returns the grouping with the correct label given active / absolute mode
 */
export const getAdjustedContributorGrouping = (grouping, isActive) => {
  if (!getIsLongShortRelated(grouping?.id)) {
    return grouping
  }

  return {
    ...grouping,
    label: isActive ? "Overweight / Underweight" : "Long / Short",
  }
}

export function getGroupingIdEquality(grouping, groupingId) {
  const id = grouping?.id
  if (!id) {
    return false
  }

  if (id === groupingId) {
    return true
  }

  if (getIsLongShortRelated(id) && getIsLongShortRelated(groupingId)) {
    return true
  }

  return false
}

export const getQueryGroupBy = (groupByOption, isActive) => {
  if (!groupByOption) {
    return null
  }

  const { id, isClassification, isTag, isPositionAttribute, classificationId } = groupByOption

  const isLongShortRelated = getIsLongShortRelated(groupByOption?.id)
  if (isLongShortRelated) {
    return { groupBy: isActive ? "OVERWEIGHT_UNDERWEIGHT" : "LONG_SHORT" }
  }

  if (!isClassification && !isTag && !isPositionAttribute) {
    return { groupBy: id }
  }

  const queryGroupBy = {
    groupById: id,
  }

  if (groupByOption.isClassification) {
    queryGroupBy.groupBy = "CLASSIFICATION"
    if (!groupByOption.proprietary) {
      queryGroupBy.groupById = classificationId
      queryGroupBy.tier = id
    }
  }

  if (groupByOption.isTag) {
    queryGroupBy.groupBy = "TAG"
  }

  if (groupByOption.isPositionAttribute) {
    queryGroupBy.groupBy = "POSITION_ATTRIBUTE"
  }

  return queryGroupBy
}

export function getSelectedGroup(groups, selectedGroup, savedGroup) {
  const group = selectedGroup || savedGroup

  // Find the first matching group data using flatMap and find
  const groupData = groups
    .flatMap(item => item.options)
    .find(option => option.id === group)

  // Return the found group data or default to first option of first group
  return groupData || get(groups, "0.options.0")
}

export const isGroupedByMembership = (grouping) => get(grouping, "isTag") || get(grouping, "isPositionAttribute")
